import { api } from '~/trpc-extra/api';
import type { TestContentMutationContext } from '../callback-handles';
import { cancelTestContentQueries, handleMutationError, handleMutationSuccess } from '../callback-handles';

export const useUpdatePageAliases = ({ testContentId }: { testContentId: string }) => {
  const trpcUtils = api.useUtils();

  return api.operation.updatePageAliases.useMutation<TestContentMutationContext>({
    onSuccess: () => handleMutationSuccess({ trpcUtils, testContentId }),
    onError: (_error, { opensPageAlias }, context) =>
      handleMutationError({
        trpcUtils,
        testContentId,
        message: `Error unable to update tab${opensPageAlias ? 's' : ''}`,
        context,
      }),
    onMutate: async ({ operationId, pageAlias, opensPageAlias }) => {
      await cancelTestContentQueries({ trpcUtils, testContentId });
      const prevData = trpcUtils.testContent.get.getData({ id: testContentId });
      if (!prevData) {
        return { prevData: null };
      }

      const targetOperation = prevData.steps
        .flatMap((step) => step.operations || [])
        .find((op) => op.id === operationId);
      if (!targetOperation) {
        return { prevData };
      }
      const targetStep = prevData.steps.find((step) => step.id === targetOperation.testStep?.id);
      if (!targetStep) {
        return { prevData };
      }

      const newData = {
        ...prevData,
        steps: prevData.steps.map((step) =>
          step.id === targetStep.id && step.operations ?
            {
              ...step,
              operations: step.operations.map((op) =>
                op.id === targetOperation.id ? { ...op, pageAlias, opensPageAlias } : op,
              ),
            }
          : step,
        ),
      };

      trpcUtils.testContent.get.setData({ id: testContentId }, newData);
      return { prevData };
    },
  });
};
